import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Baby, PlaneLanding, Cross, PlaneTakeoff, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, pad } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard } from "@/components/StatCard";
import { FieldSelect } from "@/components/FieldSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const JENIS_TONE = { Lahir: "bg-emerald-100 text-emerald-800", Datang: "bg-sky-100 text-sky-800", Meninggal: "bg-slate-800 text-white", Pindah: "bg-amber-100 text-amber-800" };

export default function Mutasi() {
  const { isAdmin } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [jenis, setJenis] = useState("");
  const nav = useNavigate();
  const load = () => api.get("/mutasi").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  const count = (j) => list.filter((m) => m.jenis === j).length;

  return (
    <div data-testid="mutasi-page">
      <PageHeader kicker="Lahir · Datang · Meninggal · Pindah" title="Mutasi Warga">
        {isAdmin && (
          <>
            <Button variant="outline" onClick={() => nav("/warga")} data-testid="mutasi-add-masuk-button"><Plus className="h-4 w-4 mr-2" /> Lahir / Datang</Button>
            <Button className="bg-[#0b4f4a] hover:bg-[#072f2c]" onClick={() => setOpen(true)} data-testid="mutasi-add-keluar-button"><Plus className="h-4 w-4 mr-2" /> Meninggal / Pindah</Button>
          </>
        )}
      </PageHeader>
      {isAdmin && <p className="text-sm text-slate-500 -mt-3 mb-5">Warga lahir atau datang didata lewat menu Data Warga → Tambah Warga, lalu pilih “Keterangan Pendataan”.</p>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testid="mutasi-stat-lahir" label="Lahir" value={count("Lahir")} icon={Baby} />
        <StatCard testid="mutasi-stat-datang" label="Datang" value={count("Datang")} icon={PlaneLanding} tone="sky" />
        <StatCard testid="mutasi-stat-meninggal" label="Meninggal" value={count("Meninggal")} icon={Cross} tone="slate" />
        <StatCard testid="mutasi-stat-pindah" label="Pindah" value={count("Pindah")} icon={PlaneTakeoff} tone="amber" />
      </div>
      <div className="bg-white border rounded-xl overflow-x-auto mt-6">
        <table className="w-full text-sm" data-testid="mutasi-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr>{["Tanggal", "Jenis", "Nama", "NIK", "RT", "Keterangan", "Dicatat oleh"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {list.map((m) => (
              <tr key={m.id} className="border-t" data-testid={`mutasi-row-${m.id}`}>
                <td className="px-4 py-3 whitespace-nowrap">{fmtDate(m.tanggal)}</td>
                <td className="px-4 py-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${JENIS_TONE[m.jenis]}`}>{m.jenis}</span></td>
                <td className="px-4 py-3 font-semibold whitespace-nowrap">{m.nama}</td>
                <td className="px-4 py-3 font-mono text-xs">{m.nik}</td>
                <td className="px-4 py-3">{pad(m.rt)}</td>
                <td className="px-4 py-3">{[m.keterangan, m.tujuan && `Tujuan: ${m.tujuan}`].filter(Boolean).join(" · ") || "-"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{m.created_by}</td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="px-4 py-14 text-center text-slate-500" data-testid="mutasi-empty">Belum ada catatan mutasi.</td></tr>}
          </tbody>
        </table>
      </div>
      <MutasiForm open={open} onOpenChange={setOpen} jenis={jenis} setJenis={setJenis} onSaved={load} />
    </div>
  );
}

const MutasiForm = ({ open, onOpenChange, jenis, setJenis, onSaved }) => {
  const [warga, setWarga] = useState([]);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ warga_id: "", tanggal: new Date().toISOString().slice(0, 10), keterangan: "", tujuan: "" });

  useEffect(() => {
    if (open) {
      api.get("/warga").then((r) => setWarga(r.data));
      setF({ warga_id: "", tanggal: new Date().toISOString().slice(0, 10), keterangan: "", tujuan: "" });
      setJenis("");
      setQ("");
    }
  }, [open, setJenis]);

  const opts = useMemo(() => {
    const s = q.trim().toUpperCase();
    return warga.filter((w) => !s || w.nama.includes(s) || w.nik.includes(s)).slice(0, 50)
      .map((w) => ({ value: w.id, label: `${w.nama} · ${w.nik} · RT ${pad(w.rt)}` }));
  }, [warga, q]);

  const submit = async (e) => {
    e.preventDefault();
    if (!jenis || !f.warga_id) return toast.error("Pilih jenis mutasi dan warga");
    try {
      await api.post("/mutasi", { ...f, jenis });
      toast.success(`Mutasi ${jenis} tercatat`);
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="mutasi-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Catat Mutasi Keluar</DialogTitle>
          <DialogDescription>Status warga akan berubah dan tidak dihitung lagi di rekap aktif.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Jenis Mutasi</Label><div className="mt-1"><FieldSelect value={jenis} onChange={setJenis} options={["Meninggal", "Pindah"]} testid="mutasi-jenis" /></div></div>
          <div>
            <Label>Cari Warga (nama / NIK)</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="mt-1" data-testid="mutasi-search-input" />
            <div className="mt-2"><FieldSelect value={f.warga_id} onChange={(v) => setF((p) => ({ ...p, warga_id: v }))} options={opts} placeholder="Pilih warga" testid="mutasi-warga-select" /></div>
          </div>
          <div><Label>Tanggal</Label><Input type="date" value={f.tanggal} onChange={(e) => setF((p) => ({ ...p, tanggal: e.target.value }))} className="mt-1" data-testid="mutasi-tanggal-input" /></div>
          {jenis === "Pindah" && <div><Label>Alamat Tujuan</Label><Input value={f.tujuan} onChange={(e) => setF((p) => ({ ...p, tujuan: e.target.value }))} className="mt-1" data-testid="mutasi-tujuan-input" /></div>}
          <div><Label>Keterangan</Label><Input value={f.keterangan} onChange={(e) => setF((p) => ({ ...p, keterangan: e.target.value }))} className="mt-1" data-testid="mutasi-keterangan-input" /></div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="mutasi-cancel-button">Batal</Button>
            <Button type="submit" className="bg-[#0b4f4a] hover:bg-[#072f2c]" data-testid="mutasi-save-button">Simpan Mutasi</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
